import React from "react";
import { formatDate, formatNumber } from "@/lib/utils";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface PlatePrintTemplateProps {
  data?: any;
  plate?: any;
  config?: any;
}

export default function PlatePrintTemplate({
  data,
  plate,
  config,
}: PlatePrintTemplateProps) {
  const d = data || plate || {};
  const c = getCompanyConfig(config);
  const docId = d?.plateId || d?.code || d?.id || "";

  // Màu kẽm thật: chuỗi (Plate.colors) hoặc { name, angle, ruling, note } (dòng kẽm của lệnh). Không mặc định CMYK.
  const colors: any[] = (Array.isArray(d.colors) ? d.colors : [])
    .map((x: any) => (typeof x === "string" ? { name: x } : x))
    .filter((x: any) => x?.name);
  const totalPlates = Number(d.totalPlates || d.colorCount || d.quantity) || colors.length;

  return (
    <div
      className="kh-print-doc bg-white w-full mx-auto p-[5mm] text-black"
      style={{
        fontFamily: "'Times New Roman', Times, serif",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
      id="print-area"
    >
      {/* 1. Header Công ty */}
      <ISOPrintHeader
        config={config}
        className="mb-3"
      />

      {/* 2. TIÊU ĐỀ BIỂU MẪU (CENTERED, NO QR) */}
      <PrintDocumentTitle
        title="LỆNH GHI KẼM CTP"
        subtitle="CTP PLATE ORDER"
        docNo={docId}
        date={d.createdAt || new Date()}
        dateLabel="Ngày xuất lệnh"
      />

      {/* 3. THÔNG TIN BÀI IN & MÁY IN (BORDERLESS 2-COL TABS) */}
      <div className="mb-4">
        <PrintInfoGrid
          labelWidth="w-[125px]"
          leftColumn={[
            {
              label: "Tên sản phẩm",
              value: d.productName || d.product?.productName || d.name || "—",
              isBold: true,
            },
            {
              label: "Tên bài in",
              value: d.printLayoutName || d.layoutName || "—",
            },
            {
              label: "Kích thước kẽm",
              value: d.sizeX && d.sizeY ? `${d.sizeX} x ${d.sizeY} cm` : d.plateSize || d.size || d.dimensions || "—",
            },
            {
              label: "Tổng số lá kẽm",
              value: totalPlates ? `${totalPlates} lá kẽm` : "—",
              isBold: true,
            },
          ]}
          rightColumn={[
            {
              label: "Mã bài kẽm",
              value: docId,
              isBold: true,
              highlight: true,
            },
            {
              label: "Khách hàng",
              value: d.customerName || d.product?.customer?.name || "—",
            },
            {
              label: "Máy in chỉ định",
              value: Array.isArray(d.machineCompat) ? d.machineCompat.join(", ") : d.machineCompat || d.machineName || "—",
              isBold: true,
            },
            {
              label: "Trạng thái",
              value: d.statusLabel || d.status || "—",
            },
          ]}
        />
      </div>

      {/* 4. Chi Tiết Màu Sắc & Nghiệm Thu Kẽm */}
      <div className="mb-4">
        <div className="bg-slate-100 font-bold text-[12px] px-2 py-1 border border-black uppercase flex justify-between items-center">
          <span>2. CHI TIẾT TỪNG LÁ KẼM & KIỂM TRA CHẤT LƯỢNG (QC INSPECTION)</span>
          <span className="text-[10px] lowercase font-normal italic">(Kiểm tra xước, độ sâu tram, thông số góc độ)</span>
        </div>
        <table className="w-full border-collapse border border-black border-t-0 text-[11px] font-sans">
          <thead>
            <tr className="bg-slate-50 border-b border-black text-slate-800">
              <th className="border-r border-black py-1.5 px-2 text-center w-10">STT</th>
              <th className="border-r border-black py-1.5 px-2 text-left w-36">Tên màu kẽm</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-24">Góc tram</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-24">Tần số tram</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-36">Kết quả kiểm tra</th>
              <th className="py-1.5 px-2 text-left">Ghi chú / Mã số lá kẽm</th>
            </tr>
          </thead>
          <tbody>
            {colors.length === 0 && (
              <tr className="h-8">
                <td colSpan={6} className="text-center italic text-slate-500">Chưa khai báo màu kẽm</td>
              </tr>
            )}
            {colors.map((color: any, idx: number) => (
              <tr key={idx} className="border-b border-black last:border-b-0 h-8">
                <td className="border-r border-black text-center font-mono">{idx + 1}</td>
                <td className="border-r border-black px-2 font-bold uppercase">{color.name}</td>
                <td className="border-r border-black text-center font-mono">{color.angle != null ? `${color.angle}°` : ""}</td>
                <td className="border-r border-black text-center font-mono">{color.ruling || ""}</td>
                <td className="border-r border-black px-2 text-center">
                  <div className="flex gap-4 justify-center text-[10px]">
                    <span className="inline-flex items-center gap-1 text-slate-600">
                      [ &nbsp; ] ĐẠT
                    </span>
                    <span className="inline-flex items-center gap-1 text-slate-400">
                      [ &nbsp; ] LỖI
                    </span>
                  </div>
                </td>
                <td className="px-2 text-slate-700">{color.note || color.notes || color.code || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 5. Ghi Chú Yêu Cầu Kỹ Thuật */}
      <div className="mb-4">
        <div className="bg-slate-100 font-bold text-[12px] px-2 py-1 border border-black uppercase">
          3. GHI CHÚ KỸ THUẬT & YÊU CẦU ĐẶC BIỆT MÁY IN
        </div>
        <div className="border border-black border-t-0 p-2.5 text-[11.5px] font-sans min-h-[45px] leading-relaxed">
          {d.notes || "Kiểm tra kỹ ốc chồng màu, thước đo tram trước khi lắp lên trục máy in. Bảo quản kẽm tránh trầy xước và ánh sáng mạnh."}
        </div>
      </div>

      {/* 6. Chữ Ký Xác Nhận */}
      <div className="grid grid-cols-4 gap-2 text-center mt-6 break-inside-avoid text-[12px] font-sans">
        <div>
          <div className="font-bold uppercase">NGƯỜI XUẤT LỆNH</div>
          <div className="italic text-slate-500 text-[11px] mb-14">(Ký & ghi rõ họ tên)</div>
          <div className="font-semibold">{d.createdBy || d.creator?.fullName || "\u00a0"}</div>
        </div>
        <div>
          <div className="font-bold uppercase">KỸ THUẬT GHI CTP</div>
          <div className="italic text-slate-500 text-[11px] mb-14">(Ký & ghi rõ họ tên)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">TRƯỞNG CA MÁY IN</div>
          <div className="italic text-slate-500 text-[11px] mb-14">(Nhận kẽm & kiểm tra)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">QUẢN ĐỐC XƯỞNG</div>
          <div className="italic text-slate-500 text-[11px] mb-14">(Phê duyệt & lưu biểu)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
      </div>
    </div>
  );
}
